import jsPDF from "jspdf";
import { useState } from "react";
import { Button, HStack, Input, Text } from "@chakra-ui/react";
import { PromptForInputModal } from "../Modals/PromptForInputModal";
import { ReportType } from "./Reports";
import { timeInMsToString, timeInMsToStringUTC, timeStringToMs } from "../Services/ResultService";
import { Gender, Participant, Result } from "../api/Dtos";

export interface Reportable {
  disciplineName: string;
  startgroup: Date | null; // Planned starttime
  gender: Gender | null;
}

function addWatermark(doc: jsPDF, watermarkText: string) {
  const totalPages = doc.internal.pages.length;
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;

  doc.setFontSize(40); // Large font size for watermark
  doc.setTextColor(200); // Light grey color for watermark
  doc.setFont("helvetica", "bold"); // Use a bold font

  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    // Apply watermark on each page
    doc.text(
      watermarkText,
      pageWidth / 2,
      pageHeight / 2,
      { angle: 45, align: "center", renderingMode: "stroke" } // Tilted and centered
    );
  }

  return doc;
}

export function createAndDownloadPdfTable(
  filename: string,
  title: string,
  headers: Array<string>,
  data: Array<Array<string | number | boolean>>,
  columnWidths: number[],
  watermark?: string,
  reportType?: ReportType
) {
  const doc = new jsPDF();
  // previous implementation
  // doc.text(title, 20, 10);
  // doc.autoTable({
  //   head: [headers.map((h) => h.replace("_", " "))],
  //   body: data,
  //   startY: 20,

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  let y = 8;

  // Add title
  doc.setFontSize(18);
  doc.text("Östhammar Friidrott - Stadsloppet", margin+42, y);
  y += 10;

  doc.setFontSize(14);
  doc.text(title, margin, y);
  y += 10;

  // Table settings
  const cellHeight = 10;
  const cellPadding = 2;
  const printWidth = pageWidth - 2 * margin;
  let currXPos = 0;
  // const columnWidth = (pageWidth - 2 * margin) / headers.length;

  // Draw header row
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  //doc.setTextColor(255, 0, 0);
  //doc.setFillColor(200, 200, 200);

  headers.forEach((header, i) => {
    doc.setFillColor(200, 200, 200);
    
    //const columnWidth = columnWidths ? columnWidths[i] : printWidth / headers.length;
    doc.rect(margin + currXPos, y, printWidth * columnWidths[i] / 100, cellHeight, "F");
    doc.text(
      header.replace("_", " "),
      margin + currXPos + cellPadding, // i * columnWidth + cellPadding,
      y + cellHeight - cellPadding,
      { maxWidth: printWidth * columnWidths[i] / 100 - 2 * cellPadding }// { maxWidth: columnWidth - 2 * cellPadding }
    );
    currXPos += printWidth * columnWidths[i] / 100; // i === 0 ? 0 : columnWidths ? columnWidths[i - 1] : printWidth / headers.length;
  });
  y += cellHeight;

  // Draw data rows
  doc.setFont("helvetica", "normal");
  doc.setFillColor(200, 200, 200);
  currXPos = 0;

  doc.text(
    "Sida " + (doc.internal.pages.length - 1).toString(),
    pageWidth - 30,
    pageHeight-4
  );

  const currentDateTime = new Date().toLocaleString('sv-SE');
  doc.text(
    currentDateTime,
    margin,
    pageHeight-4
  );
  const disciplineColIndex = headers.length; 
  const hasMultipleDisciplines = new Set(data.map(row => row[disciplineColIndex])).size > 1;
  var currentDisciplineSet = ""; // new Set<string>();

  data.forEach((row) => {
    // Check if we need a new page
    if ((y + cellHeight > pageHeight - margin) || ((reportType == ReportType.NAME_LIST || reportType == ReportType.START_NUMBER_LIST) && currentDisciplineSet != "" && hasMultipleDisciplines && currentDisciplineSet != row[disciplineColIndex].toString())) {
      doc.addPage();
      doc.text(
        "Sida " + (doc.internal.pages.length - 1).toString(),
        pageWidth - 30,
        pageHeight-4
      );
      y = margin;

      // Redraw header on new page
      doc.setFont("helvetica", "bold");
      currXPos = 0;
      headers.forEach((header, i) => {
        doc.setFillColor(200, 200, 200);
        // currXPos += printWidth * columnWidths[i] / 100;
        doc.rect(margin + currXPos, y, printWidth * columnWidths[i] / 100, cellHeight, "F");
        doc.text(
          header.replace("_", " "),
          margin + currXPos + cellPadding,
          y + cellHeight - cellPadding,
          { maxWidth: printWidth * columnWidths[i] / 100 - 2 * cellPadding }
        );
        currXPos += printWidth * columnWidths[i] / 100;
      });
      y += cellHeight;
      doc.setFont("helvetica", "normal");
    }

    if (hasMultipleDisciplines && currentDisciplineSet != row[disciplineColIndex].toString()) {
      const discipline = row[disciplineColIndex];
      doc.setFont("helvetica", "bold");
      doc.text(String(discipline), margin, y + cellHeight - cellPadding);
      doc.setFont("helvetica", "normal");
      y += cellHeight;
      currentDisciplineSet = row[disciplineColIndex].toString();
    }

    currXPos = 0;
    row.forEach((cell, i) => {
      //currXPos += printWidth * columnWidths[i] / 100;
      if (i < columnWidths.length) {
        doc.rect(margin + currXPos, y, printWidth * columnWidths[i] / 100, cellHeight);
        doc.text(
          String(cell),
          margin + currXPos + cellPadding,
          y + cellHeight - cellPadding,
          { maxWidth: printWidth * columnWidths[i] / 100 - 2 * cellPadding }
        );
        currXPos += printWidth * columnWidths[i] / 100;
      };      
    });
    y += cellHeight;
  });

  if (watermark) {
    addWatermark(doc, watermark);
  }

  doc.save(filename);
}

export function createAndDownloadPdfTablesAsSameFile(
  filename: string,
  tableLabels: Array<string>,
  headers: Array<string>,
  data: Array<Array<Array<string | number | boolean>>>,
  watermark?: string
) {
  const doc = new jsPDF();
  for (let i = 0; i < data.length; i++) {
    doc.text(tableLabels[i], 20, 10);
    doc.autoTable({
      head: [headers.map((h) => h.replace("_", " "))],
      body: data[i],
      startY: 20,
    });
    if (i != data.length - 1) {
      doc.addPage();
    }
  }

  if (watermark) {
    addWatermark(doc, watermark);
  }

  doc.save(filename);
}

export function DownloadableTablesEntry(props: {
  fileName: string;
  tableLabel: string;
  data: Array<Reportable>;
  reportType: ReportType;
  preliminary: boolean;
  onChangeTableLabel: (newTitle: string) => void;
}) {
  const [requestedDownload, setRequestedDownload] = useState(false);

  return (
    <HStack spacing={4} justify={"space-evenly"} width={"100%"}>
      <Text>Rapport:</Text>
      <Input
        width={"100%"}
        value={props.tableLabel}
        onChange={(e) => props.onChangeTableLabel(e.currentTarget.value)}
      />
      <Button
        padding={"20px"}
        onClick={() => {
          setRequestedDownload(true);
        }}
      >
        Ladda ner
      </Button>
      {requestedDownload && (
        <PromptForInputModal
          fileSuffix="pdf"
          onAbort={() => setRequestedDownload(false)}
          onConfirm={(fileName: string) => {
            setRequestedDownload(false);
            triggerSingleReportDownload(
              fileName,
              props.tableLabel,
              props.data,
              props.reportType,
              props.preliminary ? "Preliminär" : undefined
            );
          }}
          header="Report download"
          request="Ladda ner rapport som PDF med följande filnamn: "
          suggestedInput={props.tableLabel
            .replaceAll(", ", ",")
            .replaceAll(" ", "_")}
          abortButtonTitle="Avbryt"
          confirmButtonTitle="Ladda ner"
          colorScheme="green"
        />
      )}
    </HStack>
  );
}

export function triggerSingleReportDownload(
  filename: string,
  tableLabel: string,
  data: Array<Reportable>,
  reportType: ReportType,
  watermark?: string
) {
  let pdfHeaders: Array<string> = [];
  let pdfColWidths: Array<number> = [];
  let pdfData: Array<Array<string | number | boolean>> = [];
  if (reportType === ReportType.START_LIST) {
    (pdfHeaders = ["Nr", "Namn", "År", "Klubb/Lag"]),
    (pdfColWidths = [10, 40, 10, 40]),
    (pdfData = (data as Array<Participant>)
      .sort((a, b) =>
        `${a.disciplineName} ${a.firstname}`.localeCompare(
          `${b.disciplineName} ${b.firstname}`
        )
      )
      .map((d) => [
        d.startnumber ?? "-",
        `${d.firstname} ${d.lastname}`,
        d.birthYear,
        d.clubOrTeam,
        d.disciplineName,
      ]));
  } else if (reportType === ReportType.START_NUMBER_LIST) {
    (pdfHeaders = ["Nr", "Namn", "År", "Klubb/Lag"]),
    (pdfColWidths = [10, 40, 10, 40]),
      (pdfData = (data as Array<Participant>)
        .sort((a, b) =>
          `${a.disciplineName} ${a.startnumber}`.localeCompare(
            `${b.disciplineName} ${b.startnumber}`
          )
        )
        //.sort((a, b) => (a.startnumber ?? 0) - (b.startnumber ?? 0))
        .map((d) => [
          d.startnumber ?? "-",
          `${d.firstname} ${d.lastname}`,
          d.birthYear,
          d.clubOrTeam,
          d.disciplineName,
        ]));
  } else if (reportType === ReportType.RESULT_LIST) {
    const timedResults: Array<Result & { time: number }> = (data as Array<Result>)
      .filter((r) => r.startTime != null && r.finishTime != null)
      .map((r) => ({ ...r, time: r.finishTime! - r.startTime! }));

    // Check if M results exist (which means M was selected or is in the startgroup)
    const hasMResults = timedResults.some((r) => r.disciplineName === 'M');
    // Check if JL/MAN results exist
    const hasJLManResults = timedResults.some((r) => r.disciplineName === 'JL' && r.gender === 'MAN');
    
    const getSortDiscipline = (r: Result & { time: number }) =>
      r.disciplineName;

    const results: Array<Result & { time: number }> = timedResults.sort(
      (a, b) => {
        const aDiscipline = getSortDiscipline(a);
        const bDiscipline = getSortDiscipline(b);
        const disciplineCompare = aDiscipline.localeCompare(bDiscipline);
        if (disciplineCompare !== 0) return disciplineCompare;
        const timeCompare = a.time - b.time;
        if (timeCompare !== 0) return timeCompare;
        const nameA = `${a.lastname} ${a.firstname}`;
        const nameB = `${b.lastname} ${b.firstname}`;
        return nameA.localeCompare(nameB);
      }
    );

    let currentDiscipline = "";
    let indexInDiscipline = 0;
    let currentPlace = 0;
    let lastTimeInDiscipline = -1;
    let currentClubOrTeam = "";

    pdfHeaders = ["Plats", "Tid", "Namn", "År", "Klubb/Lag"];
    (pdfColWidths = [10, 12, 35, 10, 33]),
    pdfData = results.map((d) => {
      const displayDiscipline = d.disciplineName;
      
      if (displayDiscipline !== currentDiscipline) {
        currentDiscipline = displayDiscipline;
        indexInDiscipline = 1;
        currentPlace = 1;
        lastTimeInDiscipline = d.time;
        currentClubOrTeam = "";
      } else {
        if (displayDiscipline == 'HL') {
          // Hela laget har samma tid
          if (d.clubOrTeam != currentClubOrTeam) {
            indexInDiscipline += 1;
          }
        } else {
          indexInDiscipline += 1;
        }
        if (d.time !== lastTimeInDiscipline) {
          currentPlace = indexInDiscipline;
          lastTimeInDiscipline = d.time;
        }
      }

      currentClubOrTeam = d.clubOrTeam ?? "";

      const isJLInMOrKSection = (d as any).__originalDiscipline === 'JL' && (displayDiscipline === 'M' || displayDiscipline === 'K');
      const isULInPOrFSection = (d as any).__originalDiscipline === 'UL' && ['P15', 'P13', 'F15', 'F13'].includes(displayDiscipline);
      return [
        currentPlace,
        timeInMsToStringUTC(d.time),
        `${d.firstname} ${d.lastname}${isJLInMOrKSection || isULInPOrFSection ? ` (${(d as any).__originalDiscipline})` : ''}`,
        d.birthYear,
        d.clubOrTeam ?? "-",
        displayDiscipline,
      ];      
    });
  } else if (reportType === ReportType.NAME_LIST) {
    (pdfHeaders = ["Nr", "Namn", "År", "Klubb/Lag"]),
    (pdfColWidths = [10, 40, 10, 40]),
      (pdfData = (data as Array<Participant>)
        .sort((a, b) =>
          `${a.disciplineName} ${a.firstname}`.localeCompare(
            `${b.disciplineName} ${b.firstname}`
          )
        )
        .map((d) => [
          d.startnumber ?? "-",
          `${d.firstname} ${d.lastname}`,
          d.birthYear,
          d.clubOrTeam,
          d.disciplineName,
        ]));    
  }

  ///..
  createAndDownloadPdfTable(
    filename,
    tableLabel,
    pdfHeaders,
    pdfData,
    pdfColWidths,
    watermark,
    reportType
  );
}

export function triggerMultiReportDownload(
  filename: string,
  tableLabels: Array<string>,
  data: Array<Array<Reportable>>,
  reportType: ReportType,
  watermark?: string
) {
  let pdfHeaders: Array<string> = [];
  let pdfData: Array<Array<Array<string | number | boolean>>> = [];
  if (reportType === ReportType.START_LIST) {
    pdfHeaders = ["Nr", "Namn", "År", "Klubb/Lag"];
    pdfData = (data as Array<Array<Participant>>).map((t) =>
      t
        .sort((a, b) =>
          `${a.firstname} ${a.lastname}`.localeCompare(
            `${b.firstname} ${b.lastname}`
          )
        )
        .map((d) => [
          d.startnumber ?? "-",
          `${d.firstname} ${d.lastname}`,
          d.birthYear,
          d.clubOrTeam,
        ])
    );
  } else if (reportType === ReportType.START_NUMBER_LIST) {
    pdfHeaders = ["Nr", "Namn", "År", "Klubb/Lag", "Klass"];
    pdfData = (data as Array<Array<Participant>>).map((t) =>
      t
        .sort((a, b) => (a.startnumber ?? 0) - (b.startnumber ?? 0))
        .map((d) => [
          d.startnumber ?? "-",
          `${d.firstname} ${d.lastname}`,
          d.birthYear,
          d.clubOrTeam,
          d.disciplineName,
        ])
    );
  } else if (reportType === ReportType.RESULT_LIST) {
    const results: Array<Array<Result & { time: number }>> = (
      data as Array<Array<Result>>
    ).map((t) => {
      return t
        .filter((r) => r.startTime != null && r.finishTime != null)
        .map(
          (r) =>
            ({ ...r, time: r.finishTime! - r.startTime! } as Result & {
              time: number;
            })
        )
        .sort((a, b) => {
          const timeCompare = a.time - b.time;
          if (timeCompare !== 0) return timeCompare;
          const nameA = `${a.lastname} ${a.firstname}`;
          const nameB = `${b.lastname} ${b.firstname}`;
          return nameA.localeCompare(nameB);
        });
    });
    pdfHeaders = ["Plats", "Tid", "Namn", "År", "Klubb/Lag"];
    pdfData = results.map((t) => {
      return t.map((d, i) => [
        i + 1,
        timeInMsToString(d.time),
        `${d.firstname} ${d.lastname}`,
        d.birthYear,
        d.clubOrTeam ?? "-",
      ]);
    });
  } else if (reportType === ReportType.NAME_LIST) {
    pdfHeaders = ["Nr", "Namn", "År", "Klubb/Lag", "Klass"];
    pdfData = (data as Array<Array<Participant>>).map((t) =>
      t
        .sort((a, b) => (a.firstname ?? "").localeCompare(b.firstname ?? ""))
        .map((d) => [
          d.startnumber ?? "-",
          `${d.firstname} ${d.lastname}`,
          d.birthYear,
          d.clubOrTeam,
          d.disciplineName,
        ])
    );    
  }

  createAndDownloadPdfTablesAsSameFile(
    filename,
    tableLabels,
    pdfHeaders,
    pdfData,
    watermark
  );
}
